import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';
import { EllipsisV } from '@styled-icons/fa-solid';
import DataTable, { useDataTableHandle } from '../../DataTable';
import { getMasterDataCurrency, upsertMasterDataCurrency } from '../../apiCalls';
import CurrencyModal from './currencyModal';
import ConfirmationModal from '../../ConfirmationModal';
import ToastNotification from '../../Toast/ToastNotification';
import { RefDataContext } from '../Maintenance';

const Currency = () => {
  const [currency, setCurrency] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({ type: 'success', message: '' });
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);
  const [paginationLoaded, setPaginationLoaded] = useState(false);

  const dth = useDataTableHandle();
  const { registerAddHandler } = useContext(RefDataContext);

  const updateData = useCallback(async (setPaginationLength, pagination) => {
    try {
      setLoading(true);
      const pageNumber = pagination.pageNo + 1;
      const response = await getMasterDataCurrency(pageNumber, pagination.itemsPerPage, 0);
      const data = response?.data ?? (Array.isArray(response) ? response : []);
      const totalItems = response?.totalCount ?? response?.totalItems ?? data.length;
      setCurrency(data);
      setPaginationLength(totalItems);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshCurrency = useCallback(() => {
    if (!dth?.current?.pagination?.itemsPerPage) return;
    updateData(dth.current.setServerDataLength, dth.current.pagination);
  }, [updateData, dth]);

  useEffect(() => {
    registerAddHandler('currency', () => setSelectedCurrency({ id: -1 }));
  }, [registerAddHandler]);

  useEffect(() => {
    if (!paginationLoaded || !dth?.current?.pagination?.itemsPerPage) return;
    updateData(dth.current.setServerDataLength, dth.current.pagination);
  }, [paginationLoaded, updateData]);

  const handleDelete = async (row) => {
    try {
      await upsertMasterDataCurrency({
        id: row.id,
        code: row.code,
        description: row.description,
        softDelete: 1,
      });
      setShowConfirmDelete(null);
      setToastConfig({ type: 'success', message: 'Currency deleted successfully!' });
      setShowToast(true);
      refreshCurrency();
    } catch (err) {
      setToastConfig({ type: 'error', message: err.message || 'Failed to delete currency' });
      setShowToast(true);
    }
  };

  return (
    <>
      <ToastNotification
        show={showToast}
        onClose={() => setShowToast(false)}
        type={toastConfig.type}
        message={toastConfig.message}
      />

      <CurrencyModal
        currency={selectedCurrency}
        onCancel={() => setSelectedCurrency(null)}
        onSave={(isNew) => {
          setToastConfig({
            type: 'success',
            message: isNew ? 'Currency added successfully!' : 'Currency updated successfully!',
          });
          setShowToast(true);
          setSelectedCurrency(null);
          refreshCurrency();
        }}
        onError={(error) => {
          setToastConfig({ type: 'error', message: error || 'Failed to save currency' });
          setShowToast(true);
        }}
      />

      {loading && (
        <span className="d-flex align-items-center gap-2 mb-2">
          <Spinner animation="border" size="sm" /> Loading...
        </span>
      )}

      <DataTable
        idKey="id"
        handle={dth}
        data={currency}
        className="table-striped"
        lazyLoading
        onUpdate={updateData}
        onPaginationLoaded={() => setPaginationLoaded(true)}
        isLoading={loading}
      >
        <DataTable.Headers
          style={{
            fontSize: '14.4px',
            backgroundColor: '#fff',
            paddingBottom: '10px',
            borderBottom: '1px solid #ddd',
            borderTop: '1px solid #ddd',
          }}
        />
        <DataTable.Pagination itemsPerPage={[10, 25, 50, 100]} className="w-100 d-flex justify-content-end" />
        <DataTable.Row style={{ fontSize: '15px' }} />
        <DataTable.Column colKey="code" header="Currency Code" />
        <DataTable.Column colKey="description" header="Description" />
        <DataTable.Column
          colKey="actions"
          header=""
          hideFilter
          children={(item) => (
            <Dropdown className="w-100 h-100 d-grid center">
              <Dropdown.Toggle as="button" className="dot-menu-button">
                <EllipsisV width={'0.5rem'} />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item as="button" onClick={() => setSelectedCurrency(item)}>
                  View/Edit
                </Dropdown.Item>
                <Dropdown.Item as="button" onClick={() => setShowConfirmDelete(item)}>
                  Remove
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        />
      </DataTable>

      <ConfirmationModal
        header="Confirm Deletion"
        show={!!showConfirmDelete}
        onHide={() => setShowConfirmDelete(null)}
        onConfirm={() => showConfirmDelete && handleDelete(showConfirmDelete)}
      >
        Are you sure you want to delete this record?
      </ConfirmationModal>
    </>
  );
};

export default Currency;
